import type { UserPublic } from '@repo/types/users';

import { useGetGroupUsers } from '@repo/api/group/useGetGroupUsers';
import { cn } from '@repo/lib/cn';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { getUserLvl } from '@repo/lib/user-lvl/getUserLvl';
import { User } from 'lucide-react';
import { useEffect, useState } from 'react';

import GroupElementHeader from '@/components/features/groups/layout/GroupElementHeader';
import { MemberSection, MemberSectionItem } from '@/components/features/members/MemberList';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { getAvatar } from '@/utils/getAvatar';

/**
 * Props for the GroupMembers component.
 * @property {number} [groupId] - The group ID for which members are displayed.
 * @property {string} [className] - Optional CSS class for styling.
 */
interface GroupMembersProps {
  groupId?: number;
  className?: string;
}

export default function GroupMembers(groupMembersProps: GroupMembersProps) {
  const { className, groupId } = groupMembersProps;
  const [open, onOpenChange] = useState<boolean>(false);
  const { data: groupUsers = [], isLoading, isSuccess, isError, error, refetch } = useGetGroupUsers(groupId);

  useEffect(() => {
    if (isError) {
      toasterMessageHandler(baseErrorMessages, error, {
        action: (
          <Button
            size={'sm'}
            className={'ml-auto'}
            variant={'tertiary'}
            onClick={() => {
              refetch();
            }}
          >
            Ponów
          </Button>
        ),
      });
    }
  }, [error, isError]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={'md:max-w-96'}>
          <DialogBody>
            <DialogHeader className={'border-border-primary bg-card border-b'}>
              <DialogTitle>Uczestnicy grupy</DialogTitle>
            </DialogHeader>
            <div className={'flex flex-col overflow-auto p-4'}>
              <MemberSection label={'Prowadzący'}>
                {groupUsers
                  .filter((user) => user.lvl > 0)
                  .map((user) => (
                    <MemberSectionItem key={user.id} user={user} chatId={user.chatId} />
                  ))}
              </MemberSection>
              <MemberSection label={'Uczestnicy'}>
                {groupUsers
                  .filter((user) => user.lvl <= 0)
                  .map((user) => (
                    <MemberSectionItem key={user.id} user={user} chatId={user.chatId} />
                  ))}
              </MemberSection>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
      <div className={cn('border-border-primary bg-background flex flex-col rounded-2xl border', className)}>
        <GroupElementHeader label={'Uczestnicy grupy'} disabled={groupUsers.length === 0} callback={() => onOpenChange(true)} />
        <div className={'flex flex-col gap-4 overflow-y-auto overscroll-contain px-4 pb-4'}>
          {isLoading && (
            <>
              <MemberSection label={'Prowadzący'} contentClassName={'flex flex-wrap gap-1'}>
                {Array.from({ length: 3 }).map((_, index) => (
                  <GroupMemberCardSkeleton key={index} />
                ))}
              </MemberSection>
              <MemberSection label={'Uczestnicy'} contentClassName={'flex flex-wrap gap-1'}>
                {Array.from({ length: 10 }).map((_, index) => (
                  <GroupMemberCardSkeleton key={index} />
                ))}
              </MemberSection>
            </>
          )}
          {isSuccess && groupUsers.length === 0 && <p className={'text-text-tertiary w-full py-4 text-center text-sm'}>Brak członków grupy</p>}
          {isSuccess && groupUsers.length > 0 && (
            <>
              <MemberSection label={'Prowadzący'} contentClassName={'flex flex-wrap gap-1'}>
                {groupUsers
                  .filter((user) => user.lvl > 0)
                  .map((user) => (
                    <GroupMemberCard key={user.id} user={user} />
                  ))}
              </MemberSection>
              <MemberSection label={'Uczestnicy'} contentClassName={'flex flex-wrap gap-1'}>
                {groupUsers
                  .filter((user) => user.lvl <= 0)
                  .map((user) => (
                    <GroupMemberCard key={user.id} user={user} />
                  ))}
              </MemberSection>
            </>
          )}
        </div>
      </div>
    </>
  );
}

interface GroupMemberCardProps {
  user: UserPublic;
}

function GroupMemberCard(groupMemberCardProps: GroupMemberCardProps) {
  const { user } = groupMemberCardProps;
  const role = getUserLvl(user.lvl);

  const avatar: string | undefined = getAvatar(user.avatarURL);
  return (
    <Tooltip delayDuration={400}>
      <TooltipTrigger asChild>
        <Avatar size={'sm'}>
          <AvatarImage src={avatar} />
          <AvatarFallback>
            <User className={'size-4'} />
          </AvatarFallback>
        </Avatar>
      </TooltipTrigger>
      <TooltipContent className={'flex gap-2 p-2 shadow-md'} sideOffset={8}>
        <Avatar size={'sm'} className={'rounded-xs'}>
          <AvatarImage src={avatar} />
          <AvatarFallback>
            <User className={'size-3.5'} />
          </AvatarFallback>
        </Avatar>
        <div>
          <p className={'text-sm font-medium'}>
            {user.name} {user.surname}
          </p>
          <p className="text-xs opacity-75">{role}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

const GroupMemberCardSkeleton = () => {
  return <Skeleton className={'size-10 rounded-md'} />;
};
